// Objekt-Kontext für die Sidebar im Objektmodus: Kopf, Einheiten, Häuser.
// Vier Abfragen parallel, je Objekt einmal; neu laden über `neuLaden()` oder
// das Fenster-Ereignis OBJEKT_GEAENDERT (z. B. nach dem Anlegen einer Einheit).

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase/client';
import type { ObjektKopfRoh } from './objektKopf';
import {
  baueEinheitenNav, baueHaeuserNav, verwaltungsartVonObjekt,
  type EinheitNavEintrag, type EinheitRoh, type HausNavEintrag, type HausRoh, type MieterRoh, type VerwaltungsartKurz,
} from './sidebarNav';
import { heuteIso } from './format';

export const OBJEKT_GEAENDERT = 'immo-objekt-geaendert';

/** Andere Stellen melden damit, dass sich Objektdaten geändert haben. */
export function meldeObjektGeaendert(objektId: string): void {
  window.dispatchEvent(new CustomEvent<string>(OBJEKT_GEAENDERT, { detail: objektId }));
}

export interface ObjektKontext {
  kopf: ObjektKopfRoh;
  verwaltungsart: VerwaltungsartKurz | null;
  einheiten: EinheitNavEintrag[];
  haeuser: HausNavEintrag[];
}

export type KontextZustand =
  | { status: 'laedt' }
  | { status: 'fehler'; meldung: string }
  | { status: 'fehlt' }
  | { status: 'ok'; daten: ObjektKontext };

async function lade(objektId: string): Promise<KontextZustand> {
  const [obj, ein, haus, mie] = await Promise.all([
    supabase.from('objekte').select('*').eq('id', objektId).maybeSingle(),
    supabase.from('objekt_einheiten').select('id, bezeichnung, haus_id').eq('objekt_id', objektId),
    supabase.from('objekt_haeuser').select('id, bezeichnung').eq('objekt_id', objektId),
    supabase.from('mieter')
      .select('einheit_id, status, kaltmiete, nebenkosten_vorauszahlung, heizkosten, weitere_kosten, miet_historie')
      .eq('objekt_id', objektId),
  ]);
  const fehler = obj.error ?? ein.error ?? haus.error ?? mie.error;
  if (fehler) return { status: 'fehler', meldung: fehler.message };
  if (!obj.data) return { status: 'fehlt' };
  const kopf = obj.data as ObjektKopfRoh;
  const einheiten = baueEinheitenNav((ein.data ?? []) as EinheitRoh[], (mie.data ?? []) as MieterRoh[], heuteIso());
  return {
    status: 'ok',
    daten: {
      kopf,
      verwaltungsart: verwaltungsartVonObjekt(kopf),
      einheiten,
      haeuser: baueHaeuserNav((haus.data ?? []) as HausRoh[], einheiten),
    },
  };
}

export function useObjektKontext(objektId: string | null): { zustand: KontextZustand; neuLaden: () => void } {
  const [zustand, setZustand] = useState<KontextZustand>({ status: 'laedt' });
  const [runde, setRunde] = useState(0);
  const neuLaden = useCallback(() => setRunde(r => r + 1), []);

  useEffect(() => {
    if (!objektId) return;
    let aktiv = true;
    // Beim Objektwechsel neu laden, beim reinen Aktualisieren alten Stand stehen lassen
    setZustand(z => (z.status === 'ok' && z.daten.kopf.id === objektId ? z : { status: 'laedt' }));
    lade(objektId)
      .then(z => { if (aktiv) setZustand(z); })
      .catch((e: unknown) => { if (aktiv) setZustand({ status: 'fehler', meldung: e instanceof Error ? e.message : 'Unbekannter Fehler' }); });
    return () => { aktiv = false; };
  }, [objektId, runde]);

  useEffect(() => {
    if (!objektId) return;
    const bei = (e: Event) => {
      const id = (e as CustomEvent<string>).detail;
      if (!id || id === objektId) neuLaden();
    };
    window.addEventListener(OBJEKT_GEAENDERT, bei);
    return () => window.removeEventListener(OBJEKT_GEAENDERT, bei);
  }, [objektId, neuLaden]);

  return { zustand, neuLaden };
}
